import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigation } from 'context/NavigationContext';
import { BringzzPageContainer, BringzzInput, BringzzIcon, BringzzText } from "@bringzz/components";
import SearchFooter from './SearchFooter';
import SearchModal from './SearchModal';
import { alphabets } from './constants';
import classNames from 'classnames';
import useRequest from 'hooks/useRequest';
import { useTranslation } from 'react-i18next';

const MIN_HEADER_HEIGHT = 100;

const ProductCategoryPage = () => {
    const { t } = useTranslation();
    const { categoryUUID } = useParams();
    const { navigate, backPress } = useNavigation();
    const { sendRequest } = useRequest();
    const [categoryName, setCategoryName] = useState('');
    const [subCategories, setSubCategories] = useState([]);
    const [loadingChildren, setLoadingChildren] = useState(false);
    const [alphabetFilter, setAlphabetFilter] = useState('#');
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

    useEffect(() => {
        if (!categoryUUID) return;

        // Extract category name from UUID (format: category_Name_uuid)
        const parts = categoryUUID.split('_');
        if (parts.length >= 2) {
            setCategoryName(decodeURIComponent(parts.slice(1, -1).join(' ')));
        }

        // Reset state for new category
        setSubCategories([]);
        setAlphabetFilter('#');

        const fetchCategoryData = async () => {
            setLoadingChildren(true);
            try {
                const response = await sendRequest({
                    method: 'GET',
                    url: `/module/getCategoryChildren/productcategory?categoryUUID=${encodeURIComponent(categoryUUID)}`,
                });

                if (response.status === 'success' && response.data) {
                    setSubCategories(response.data.categories || []);
                }
            } catch (err) {
                console.error('Error fetching category data:', err);
            } finally {
                setLoadingChildren(false);
            }
        };

        fetchCategoryData();
    }, [categoryUUID]);

    const handleSubCategoryClick = (subCategory) => {
        if (subCategory.isCurrentCategory || subCategory.hasChildren === false) {
            navigate(`/search/product-category/${encodeURIComponent(subCategory.categoryUUID)}/products`);
        } else {
            navigate(`/search/product-category/${encodeURIComponent(subCategory.categoryUUID)}`);
        }
    };

    const filteredCategories = subCategories.filter((cat) => {
        if (alphabetFilter === '#' || alphabetFilter === 'All') {
            return true;
        }
        return cat.category.toUpperCase().startsWith(alphabetFilter);
    });

    const isLoading = loadingChildren && subCategories.length === 0;

    return (
        <BringzzPageContainer
            className="bg-sand"
            footerHover={true}
            footer={
                <div className='p-4'>
                    <BringzzInput
                        className='shadow-xl border-slate-400 bg-gray-100 cursor-pointer'
                        placeholder={t('component.search.general.find_items')}
                        readOnly
                        onClick={() => setIsSearchModalOpen(true)}
                    />
                </div>
            }
        >
            <div className='relative'>
                {/* Header */}
                <div
                    className='fixed w-full px-4 top-0 z-50 bg-white max-w-xl flex flex-col items-center justify-center border-b shadow-md space-y-2 py-4'
                    style={{ height: `${MIN_HEADER_HEIGHT}px` }}
                >
                    <div className='flex justify-between w-full items-center'>
                        <BringzzIcon
                            folder='LineIcons'
                            icon='IconChevronLeft'
                            size='22'
                            onClick={() => backPress()}
                            className='cursor-pointer'
                        />
                        <BringzzText tag='h2' className='text-lg'>
                            {categoryName}
                        </BringzzText>
                        <div style={{ width: 22 }}></div>
                    </div>
                </div>

                <div style={{ paddingTop: `${MIN_HEADER_HEIGHT}px` }}>
                    {isLoading ? (
                        <div className="flex items-center justify-center p-8">
                            <BringzzIcon
                                size="32"
                                icon="IconSpinner"
                                folder="LineIcons"
                                className="animate-spin text-magic-lilac"
                            />
                        </div>
                    ) : (
                        <>
                            {/* Sub-categories section */}
                            {subCategories.length > 0 && (
                                <div className='rounded-xl bg-white mx-2 py-2'>
                                    <div className='px-4 py-2'>
                                        <BringzzText tag='h3'>
                                            {t('component.search.product_categories.subcategories') || 'Subcategories'}
                                        </BringzzText>
                                    </div>
                                    {/* Alphabet filter */}
                                    <div className='flex items-center overflow-x-auto sticky top-0 bg-white z-10 py-2' style={{ paddingLeft: '12px', paddingRight: '12px' }}>
                                        <BringzzText tag='h3' className='whitespace-nowrap mr-1'>{t('component.search.general.alphabet_filter')}</BringzzText>
                                        <div className='flex space-x-3 sm:space-x-2 min-w-0'>
                                            {alphabets.map((char, index) => {
                                                const isSelected = char === alphabetFilter;
                                                return (
                                                    <div
                                                        key={index}
                                                        className={classNames('bg-sand p-2 rounded-full cursor-pointer flex-shrink-0',
                                                            isSelected && "!bg-magic-lilac"
                                                        )}
                                                        onClick={() => setAlphabetFilter(char)}
                                                    >
                                                        <BringzzText tag='h5'>{char}</BringzzText>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Sub-category list */}
                                    <div>
                                        {filteredCategories.map((subCategory, index) => (
                                            <div
                                                key={subCategory.categoryUUID}
                                                className={classNames(
                                                    'h-12 cursor-pointer flex justify-between px-6 items-center',
                                                    index !== filteredCategories.length - 1 && 'border-b',
                                                    'hover:bg-magic-lilac/10 transition-colors duration-300'
                                                )}
                                                onClick={() => handleSubCategoryClick(subCategory)}
                                            >
                                                <div className='flex space-x-3 items-center'>
                                                    <BringzzText tag='h4'>{subCategory.category}</BringzzText>
                                                </div>
                                                <BringzzIcon
                                                    size="16"
                                                    icon="IconChevronRight"
                                                    folder="LineIcons"
                                                    className="text-gray-400"
                                                />
                                            </div>
                                        ))}
                                        {filteredCategories.length === 0 && (
                                            <div className='px-6 py-4'>
                                                <BringzzText tag='p'>
                                                    {t('component.search.product_categories.no_subcategories') || 'No subcategories found'}
                                                </BringzzText>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Empty state: no sub-categories */}
                            {subCategories.length === 0 && !loadingChildren && (
                                <div className='rounded-xl bg-white mx-2 py-8'>
                                    <div className='px-6 text-center'>
                                        <BringzzText tag='p'>
                                            {t('component.search.product_categories.no_subcategories') || 'No subcategories found'}
                                        </BringzzText>
                                    </div>
                                </div>
                            )}

                            <SearchFooter />
                        </>
                    )}
                </div>
            </div>
            <SearchModal
                isOpen={isSearchModalOpen}
                onClose={() => setIsSearchModalOpen(false)}
            />
        </BringzzPageContainer>
    );
};

export default ProductCategoryPage;
